import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Music, Search, UserRound, X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { usePlayer } from '../context/PlayerContext';
import { API_BASE as API } from '../lib/apiBase';




function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}


function getArtistGenres(artist) {
  if (!artist?.genre) return [];
  if (Array.isArray(artist.genre)) return artist.genre.filter(Boolean);
  return String(artist.genre).split(',').map((item) => item.trim()).filter(Boolean);
}


export default function SearchPage() {
  const { lang } = useLang();
  const { playSong } = usePlayer();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ artists: [], songs: [], events: [] });
  const [loading, setLoading] = useState(false);

  const trimmedQuery = useMemo(() => query.trim(), [query]);

  useEffect(() => {
    setQuery(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    const activeQuery = (searchParams.get('q') || '').trim();
    if (!activeQuery) {
      setResults({ artists: [], songs: [], events: [] });
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/search?q=${encodeURIComponent(activeQuery)}&limit=8`)
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) {
          setResults({
            artists: data.artists || [],
            songs: data.songs || [],
            events: data.events || [],
          });
        }
      })
      .catch(() => {
        if (!cancelled) setResults({ artists: [], songs: [], events: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const submitSearch = (event) => {
    event.preventDefault();
    const nextQuery = query.trim();
    if (!nextQuery) {
      navigate('/');
      return;
    }
    setSearchParams({ q: nextQuery }, { replace: false });
  };

  const totalResults = results.artists.length + results.songs.length + results.events.length;

  return (
    <div data-testid="search-page" className="min-h-screen px-4 md:px-10 pt-24 pb-28">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-[32px] border border-white/10 bg-black/35 p-6 md:p-8 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-3">PulseMusic</p>
          <h1 data-testid="search-page-title" className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            {lang === 'es' ? 'Buscar artistas, canciones y eventos' : 'Search artists, songs and events'}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-white/70 leading-relaxed">
            {lang === 'es' ? 'Búsqueda simple, directa y fiable. Si existe en PulseMusic, debería aparecer aquí.' : 'Simple, direct and reliable search. If it exists in PulseMusic, it should appear here.'}
          </p>

          <form onSubmit={submitSearch} className="mt-6 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
              <input
                data-testid="search-page-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={lang === 'es' ? 'Prueba con artista, canción o evento' : 'Try an artist, song or event'}
                className="w-full rounded-2xl border border-pulse-border bg-pulse-surface pl-12 pr-12 py-4 text-base focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              />
              {query && (
                <button data-testid="search-page-clear" type="button" onClick={() => setQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-pulse-muted hover:text-white transition-colors duration-200">
                  <X size={18} />
                </button>
              )}
            </div>
            <button data-testid="search-page-submit" type="submit" className="rounded-full bg-pulse-primary px-6 py-4 text-sm font-bold text-black">
              {lang === 'es' ? 'Buscar' : 'Search'}
            </button>
          </form>
        </div>

        {loading && (
          <div data-testid="search-page-loading" className="flex items-center justify-center py-16">
            <div className="w-10 h-10 rounded-full border-2 border-pulse-primary border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && !trimmedQuery && (
          <div data-testid="search-page-idle" className="rounded-[28px] border border-white/10 bg-pulse-surface mt-8 p-8 text-sm text-white/70">
            {lang === 'es' ? 'Empieza con el nombre de un artista, una canción o un evento.' : 'Start with the name of an artist, song or event.'}
          </div>
        )}

        {!loading && trimmedQuery && totalResults === 0 && (
          <div data-testid="search-page-empty" className="rounded-[28px] border border-white/10 bg-pulse-surface mt-8 p-8 text-center">
            <p className="font-heading text-2xl font-bold mb-2">{lang === 'es' ? 'No encontramos resultados útiles' : 'We could not find useful results'}</p>
            <p className="text-sm text-white/70 leading-relaxed max-w-xl mx-auto">
              {lang === 'es' ? 'Prueba con menos palabras, un nombre artístico más corto o el título principal de una canción o evento.' : 'Try fewer words, a shorter artist name, or the main title of a song or event.'}
            </p>
          </div>
        )}

        {!loading && totalResults > 0 && (
          <div className="space-y-8 mt-8">
            {results.artists.length > 0 && (
              <section data-testid="search-artists-section" className="rounded-[28px] border border-white/10 bg-pulse-surface p-6">
                <div className="flex items-center gap-2 mb-5">
                  <UserRound size={16} className="text-pulse-primary" />
                  <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Artistas' : 'Artists'}</h2>
                </div>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {results.artists.map((artist) => {
                    const genres = getArtistGenres(artist);
                    return (
                      <Link key={artist.id} to={`/artist/${artist.id}`} data-testid={`search-artist-${artist.id}`} className="rounded-[24px] border border-white/10 bg-black/20 p-4 hover:border-white/20 transition-colors duration-300">
                        <div className="flex items-center gap-3">
                          {artist.photo ? <img src={artist.photo} alt={artist.name} className="w-14 h-14 rounded-2xl object-cover" /> : <div className="w-14 h-14 rounded-2xl bg-pulse-elevated" />}
                          <div className="min-w-0 flex-1">
                            <p className="font-heading text-lg font-semibold truncate">{artist.name}</p>
                            <p className="text-xs text-pulse-muted truncate">{genres.join(' · ') || artist.city || (lang === 'es' ? 'Perfil artista' : 'Artist profile')}</p>
                            <p className="text-[11px] text-pulse-muted mt-1">{formatNum(artist.plays)} plays</p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {results.songs.length > 0 && (
              <section data-testid="search-songs-section" className="rounded-[28px] border border-white/10 bg-pulse-surface p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Music size={16} className="text-pulse-primary" />
                  <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Canciones' : 'Songs'}</h2>
                </div>
                <div className="space-y-3">
                  {results.songs.map((song, index) => {
                    const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                    return (
                      <button key={song.id} data-testid={`search-song-${song.id}`} onClick={() => playSong(song, results.songs, index)} className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left flex items-center gap-3 hover:border-white/20 transition-colors duration-300">
                        <img src={song.cover} alt={title} className="w-12 h-12 rounded-xl object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{title}</p>
                          <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
                        </div>
                        <span className="text-xs text-pulse-muted">{formatNum(song.plays)} plays</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {results.events.length > 0 && (
              <section data-testid="search-events-section" className="rounded-[28px] border border-white/10 bg-pulse-surface p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar size={16} className="text-pulse-primary" />
                  <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Eventos' : 'Events'}</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {results.events.map((event) => {
                    const title = lang === 'en' ? (event.title_en || event.title_es) : event.title_es;
                    return (
                      <Link key={event.id} to="/events" data-testid={`search-event-${event.id}`} className="rounded-[24px] border border-white/10 bg-black/20 p-4 hover:border-white/20 transition-colors duration-300">
                        <div className="flex gap-3">
                          <img src={event.image} alt={title} className="w-20 h-20 rounded-2xl object-cover" />
                          <div className="min-w-0 flex-1">
                            <p className="font-heading text-lg font-semibold leading-tight">{title}</p>
                            <p className="text-xs text-pulse-muted mt-2 inline-flex items-center gap-1"><MapPin size={12} /> {event.location?.name || event.location?.venue || (lang === 'es' ? 'Ubicación por confirmar' : 'Location TBA')}</p>
                            <p className="text-xs text-pulse-muted mt-1">{event.date ? new Date(event.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}